import {ProductPriceData} from './product-price-data';

/**
 * A price that applies for part of the year.
 *
 * Trades whose costs move with the calendar — landscaping, roofing, snow
 * clearance, anything weather-bound — had one price per product and had to
 * remember to edit it twice a year.
 *
 * Pricing is resolved against the date the estimate is produced, so re-opening
 * an old project in another season re-prices it at today's rate. Pinning a
 * quote to the date it was issued is quote versioning, which is a separate
 * feature; ProductsService.pricingDate is the seam it would use.
 *
 * The window is a RECURRING month/day range, not absolute dates, so it is
 * entered once rather than every year. A range may wrap the year end
 * (Nov 15 → Feb 28), which is the normal shape for a winter rate.
 */
export class ProductSeason {
  id = '';
  /** What the contractor calls it — "Peak season", "Winter rate". */
  name = '';
  /** Inclusive start, 1-12. */
  startMonth = 1;
  /** Inclusive start day, 1-31. */
  startDay = 1;
  /** Inclusive end, 1-12. */
  endMonth = 12;
  /** Inclusive end day, 1-31. */
  endDay = 31;
  /** Overrides the product's area pricing while the season is in force. */
  pricing?: ProductPriceData;
  /** Overrides the product's distance pricing while the season is in force. */
  distancePricing?: ProductPriceData;
  enabled = true;
}

/** Day-of-year style key that compares correctly: month * 100 + day. */
function monthDayKey(month: number, day: number): number {
  return (Math.max(1, Math.min(12, month | 0)) * 100) + Math.max(1, Math.min(31, day | 0));
}

/**
 * Whether `date` falls inside the season's recurring window.
 *
 * Handles the wrap-around case (start after end, e.g. Nov 15 → Feb 28) by
 * treating it as "on or after the start, OR on or before the end" rather than
 * a single interval — the naive `start <= x && x <= end` yields nothing at all
 * for a winter rate.
 */
export function seasonCoversDate(season: ProductSeason, date: Date): boolean {
  if (!season || season.enabled === false) {
    return false;
  }
  const at = monthDayKey(date.getMonth() + 1, date.getDate());
  const from = monthDayKey(season.startMonth, season.startDay);
  const to = monthDayKey(season.endMonth, season.endDay);
  return from <= to ? (at >= from && at <= to) : (at >= from || at <= to);
}

/**
 * The season in force on `date`, or null.
 *
 * First match wins, so an earlier entry beats a later one where two overlap.
 * Overlaps are not rejected: the contractor's own ordering is a clearer rule
 * than refusing to save, and the editor lists them in order.
 */
export function seasonInForce(seasons: ProductSeason[], date: Date): ProductSeason | null {
  if (!Array.isArray(seasons)) {
    return null;
  }
  return seasons.find(s => seasonCoversDate(s, date)) ?? null;
}
